import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ECPlantTip, ECPlantTipMode, ECPlantTipType} from 'smag-commons/dto/eco';
import {limitDecimals} from '../../../../shared/ec-economics';
import {isDefined} from '../../../../commons/commons';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

export interface TipResponse extends ECPlantTip {
    year: number;
}


@Component({
    selector: 'app-set-tip',
    templateUrl: './set-tip.component.html',
    styleUrls: ['./set-tip.component.scss']
})
export class SetTipComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private response: TipResponse = {} as TipResponse;
    form: FormGroup;
    step = 1;

    @Input() grossTip: number;
    @Input() netTip: number;
    @Input() nonSurplusTip: number;
    @Input() tipConfigs: ECPlantTip;
    @Input() year: number;
    @Input() TSI: number;
    @Input() ECNE: number;
    @Input() incentiveSharedEnergy: number;
    @Input() massiveConfig: boolean;
    @Input() capitalGrantUse: boolean;
    @Output() tipResponse: EventEmitter<TipResponse> = new EventEmitter<TipResponse>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();

    readonly limitDecimals = limitDecimals;
    readonly ECPlantTipType = ECPlantTipType;
    readonly ECPlantTipMode = ECPlantTipMode;

    constructor() {
        this.form = new FormGroup({
            tipUsage: new FormControl(null, [Validators.required]),
            tipMode: new FormControl(null, [Validators.required]),
            percentage: new FormControl(null,
                Validators.compose([Validators.min(0), Validators.max(100)])),
            eurMegawatt: new FormControl(null, [Validators.min(0)])
        }, {validators: [validateForm]});
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    ngOnInit(): void {
        this.form.controls.tipUsage.setValue(isDefined(this.tipConfigs) ? this.tipConfigs.type : null);
        this.form.controls.tipMode.setValue(isDefined(this.tipConfigs) ? this.tipConfigs.mode : null);
        this.form.controls.percentage.setValue(isDefined(this.tipConfigs) && this.tipConfigs.mode === ECPlantTipMode.PERCENTAGE ? this.tipConfigs.plant_tip * 100 : null);
        this.form.controls.eurMegawatt.setValue(isDefined(this.tipConfigs) && this.tipConfigs.mode === ECPlantTipMode.EURO_MGW ? this.tipConfigs.plant_tip : null);

        this.form.controls.tipUsage.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.resetValueFields());

        this.form.controls.tipMode.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.resetValueFields());

        this.form.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                if (this.form.valid) {
                    this.response = {
                        year: this.year,
                        type: this.form.controls.tipUsage.value,
                        mode: this.form.controls.tipMode.value,
                        plant_tip: this.form.controls.tipMode.value === ECPlantTipMode.PERCENTAGE ? this.form.controls.percentage.value / 100 : this.form.controls.eurMegawatt.value,
                    } as TipResponse;
                    this.tipResponse.next(this.response);
                }
                this.valid.next(this.form.valid);
            });
    }

    resetValueFields(): void {
        this.form.controls.percentage.setValue(null);
        this.form.controls.eurMegawatt.setValue(null);
    }

    onClickNextStep() {
        this.step++;
    }

    onClickGoToStep(newStep: number) {
        if (newStep) {
            this.step = newStep;
        }
    }

    computePlantTip(mode: ECPlantTipMode, type: ECPlantTipType): number {
        if (!isDefined(mode)) {
            return 0;
        }
        if (mode === ECPlantTipMode.PERCENTAGE) {
            if (!isDefined(this.form.controls.percentage.value)) {
                return 0;
            }
            return (type === ECPlantTipType.GROSS ? this.grossTip : type === ECPlantTipType.NET ? this.netTip : this.nonSurplusTip) * this.form.controls.percentage.value / 100;
        }
        if (!isDefined(this.form.controls.eurMegawatt.value)) {
            return 0;
        }
        return ((((type === ECPlantTipType.GROSS || type === ECPlantTipType.NET) ? this.incentiveSharedEnergy : this.ECNE) / 1000) * this.form.controls.eurMegawatt.value);
    }

}

function validateForm(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;

    if (!ctrls.tipUsage.value) {
        errs.tip_usage = true;
    }
    if (!ctrls.tipMode.value) {
        errs.tip_mode = true;
    }

    if (ctrls.tipMode.value === ECPlantTipMode.PERCENTAGE) {
        if (!isDefined(ctrls.percentage.value)) {
            errs.percentage = true;
        }
    } else if (ctrls.tipMode.value === ECPlantTipMode.EURO_MGW) {
        if (!isDefined(ctrls.eurMegawatt.value)) {
            errs.eurMegawatt = true;
        }
    }
    return errs;
}
